import { engineComponent, NumberUtils } from "neo-common";
import { Communication } from "neo-games";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../../Utils/FrameTimer";
import { Paylines } from "../Paylines";
import { Symbols } from "../Symbols";
import { Machine } from "../Machine";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { StageController } from "../../Components/StageController";
import { WinAnimator } from "../WinAnimator";
import { FreespinsController } from "./FreespinsController";
import { GameManager } from "../../Managers/GameManager";
import { PlayList } from "../../Components/Sounds/PlayList";
import { HtmlController } from "../../HTMLComponents/HtmlController";
import { DataManager } from "../../Managers/DataManager";
import { Tweens } from "../../Components/Effects/Tweens";
import { WinAnimatorExtendedFS } from "../../CustomComponents/WinAnimatorExtendedFS";
import { GroupTweens } from "../../CustomComponents/GroupTweens";
import { AmbientBackground } from "../../Utils/AmbientBackground";
import { Settings } from "../../CustomComponents/Settings";
import { MachineExtended } from "../../CustomComponents/MachineExtended";
import { CustomUtils } from "../../Utils/CustomUtils";
import { Scaling } from "../../Utils/Scaling";
import { WinAnimatorExtended } from "../../CustomComponents/WinAnimatorExtended";
import { TextElement } from "../../Components/Elements/Texts/TextElement";
import { LayoutContainerElement } from "../../CustomComponents/LayoutContainerElement";
import { ImageTextCounterElement } from "../../Components/Elements/Texts/ImageTextCounterElement";
import { ImageElement } from "../../Components/Elements/Graphics/ImageElement";
import { ImageTextElement } from "../../Components/Elements/Texts/ImageTextElement";
import { GameData } from "../../Channels/Common/Datas/GameData";
import { AppContext } from "../../../Common/AppContext";

@engineComponent(ComponentTypes.DisplayObject)
export class FreespinsView extends LayoutContainerElement {
    static INTRO_DELAY: number = 250;
    static OUTRO_DELAY: number = 60;

    private _respin: boolean = false;

    private _controller: FreespinsController;

    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;
    private _machineStoppedDelayTimer: FrameTimerEventEmitter;
    private _mysteryDelayTimer: FrameTimerEventEmitter;
    private _fsMessageEndDelayTimer: FrameTimerEventEmitter;

    private _paylines: Paylines;
    private _symbols: Symbols;
    private _machine: MachineExtended;
    private _winAnimator: WinAnimatorExtendedFS;
    private _noWinContainer: ContainerElement;
    private _winContainer: ContainerElement;
    private _groupTweens: SwitchElement;
    private _multiplierValueElement: ImageTextElement;
    private _megaWaysSwitch: SwitchElement;
    private _fsMessageEndValueElement: ImageTextElement;

    private _machineStopped = true;

    private _fsMessageStart: HTMLElement;
    private _fsMessageEnd: HTMLElement;
    private _fsMessageEndNoWin: HTMLElement;

    private modeSetting: { Landscape?: Node; Portrait?: Node; Extended?: Node } = {};

    constructor() {
        super();

        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        this._machineStoppedDelayTimer = CreateFrameTimer("machineStoppedDelayTimer");
        this._machineStoppedDelayTimer.on(FrameTimerEvents.Complete, this.machineStoppedDelayed.bind(this));

        this._mysteryDelayTimer = CreateFrameTimer("mysteryDelayTimer");
        this._mysteryDelayTimer.on(FrameTimerEvents.Complete, this.mysterySymbol.bind(this));

        this._fsMessageEndDelayTimer = CreateFrameTimer("fsMessageEndDelayTimer");
        this._fsMessageEndDelayTimer.on(
            FrameTimerEvents.Complete,
            function () {
                this.fsMessageEnd(0);
            }.bind(this),
        );

        StageController.freespinsStageContainer = this;
    }

    create(node: Element): void {
        super.create(node);

        if (this.childs != null) {
            for (let key in this.childs) {
                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                    this._paylines.reset();
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof MachineExtended) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimatorExtendedFS) {
                    this._winAnimator = this.childs[key];
                }

                if (key === "NoWinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._noWinContainer = this.childs[key];
                }

                if (key === "WinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._winContainer = this.childs[key];
                }

                if (key === "MegaWays" && this.childs[key] instanceof SwitchElement) {
                    this._megaWaysSwitch = this.childs[key];
                }

                if (key === "Multiplier" && this.childs[key] instanceof SwitchElement) {
                    this._multiplierValueElement = (<SwitchElement>this.childs[key]).getChildElementByName(
                        "Value",
                    ) as ImageTextElement;
                }

                if (key === "MessageEnd" && this.childs[key] instanceof SwitchElement) {
                    let valueElement = (<ContainerElement>(
                        (<SwitchElement>this.childs[key]).getChildElementByName("Con")
                    )).getChildElementByName("Value");

                    if (valueElement != null && valueElement instanceof ImageTextElement) {
                        this._fsMessageEndValueElement = valueElement;
                    }
                }

                if (key === "GroupTweens" && this.childs[key] instanceof SwitchElement) {
                    this._groupTweens = this.childs[key]; // SwitchElement with 2 elements of GroupTweens
                }
            }

            // set childs to GroupTweens object
            if (this._groupTweens) {
                let groupTweenElements: Object = this._groupTweens.getChildElements();
                if (groupTweenElements) {
                    for (let key in groupTweenElements) {
                        if (groupTweenElements.hasOwnProperty(key)) {
                            if (groupTweenElements[key] instanceof GroupTweens) {
                                (<GroupTweens>groupTweenElements[key]).setChilds(this.childs);
                            }
                        }
                    }
                }
            }

            this._fsMessageStart = document.getElementById("fsMessageStart");
            this._fsMessageEnd = document.getElementById("fsMessageEnd");
            this._fsMessageEndNoWin = document.getElementById("fsMessageEndNoWin");

            //prevent first animation on page load
            this._fsMessageStart.classList.add("_noAnim");
            this._fsMessageEnd.classList.add("_noAnim");
            this._fsMessageEndNoWin.classList.add("_noAnim");

            if (DEBUG) {
                if (this._paylines == null) {
                    this.log("WARNING no paylines");
                }

                if (this._symbols == null) {
                    this.log("ERROR no symbols");
                }

                if (this._machine == null) {
                    this.log("ERROR no machine");
                }

                if (this._winAnimator == null) {
                    this.log("WARNING no winAnimator");
                }

                if (this._fsMessageStart == null) {
                    this.log("ERROR no fsMessageStart");
                }

                if (this._fsMessageEnd == null) {
                    this.log("ERROR no fsMessageEnd");
                }

                if (this._fsMessageEndNoWin == null) {
                    this.log("ERROR no fsMessageEndNoWin");
                }
            }
        }

        this.visible = false;
    }

    initReceived(controller: FreespinsController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.freespinsResponse != null) {
                    if (
                        GameManager.freespinsResponse.initData != null &&
                        GameManager.freespinsResponse.initData.reels !== null
                    ) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.on(MachineExtended.SpinReelRollFinished, this.onSpinReelRollFinished.bind(this));
                        this._machine.on(MachineExtended.RespinReelStopped, this.onRespinReelStopped.bind(this));
                        this._machine.on(MachineExtended.RespinRollFinished, this.respinRollFinished.bind(this));
                        this._machine.on(MachineExtended.RespinTeaseStart, this.respinTeaseStart.bind(this));
                        this._machine.initialize(
                            this._symbols,
                            GameManager.freespinsResponse.initData.reels,
                            this._winAnimator,
                            this.childs["Teasing"],
                            this.childs["TeasingBlack"],
                            this.childs["NoTrimContainer"],
                            this.childs["NoTrimTeasingContainer"],
                            this.childs["ReelTopContainer"],
                        );
                    }

                    if (this._winAnimator != null) {
                        this._winAnimator.on(WinAnimator.FinishWinCalculation, this.finishWinCalculation.bind(this));
                        this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                        this._winAnimator.on(
                            WinAnimatorExtended.FinishQuickWinCalculation,
                            this.finishQuickWinCalculation.bind(this),
                        );
                        this._winAnimator.on(
                            WinAnimatorExtended.UpdateRemainingFreespinsCount,
                            this.updateRemainingFreespinsCount.bind(this),
                        );
                        this._winAnimator.on(WinAnimatorExtended.LimitWinCounter, this.limitWinCounter.bind(this));
                        this._winAnimator.initialize(
                            this._symbols,
                            this._paylines,
                            this._noWinContainer,
                            <ContainerElement>this._winContainer.getChildElementByName("NestedWinContainer"),
                            this,
                        );
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no freespin reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }

        this.visible = false;
    }

    initIntro(): void {
        StageController.baseStage = false;
        StageController.pickStage = false;
        StageController.freespinStage = true;

        this._machine.setStoppedSymbolsArray(GameManager.freespinsResponse.screen.reels);
        this._winAnimator.showSymbols(GameManager.freespinsResponse.screen.reels, true);
        this._winAnimator.showInitWins(
            GameManager.freespinsResponse.wins,
            GameManager.freespinsResponse.currentWin,
            GameManager.freespinsResponse.totalWin,
            GameManager.freespinsResponse.customData != null
                ? GameManager.freespinsResponse.customData[Settings.CustomDataRoundTotalWin]
                : 0,
        );
        this._winAnimator.updateCoinsCollector(true); // INIT coins collector
        this._winAnimator.updateFreespinsCount(Settings.remainingFreespinsCount());
        this._winAnimator.recountingWaysCounter(-1); // -1 get from reels

        if (this._multiplierValueElement) {
            this._multiplierValueElement.setText("x" + GameManager.freespinsResponse.freeSpins.multiplier);
        }

        if (this._groupTweens) {
            this._groupTweens.goToCase(GroupTweens.CaseInitIntro);
        }

        this.visible = true;

        // AmbientBackground.changeAmbientBackground('color-1', 0);

        if (this.isLastFreespin() && !Settings.isRespin()) {
            PlayList.stop("SND_FSLoop", 5);
            PlayList.loop("SND_FSWait", 5);
            PlayList.play("SND_FSEnd");

            this.fsMessageEnd(0, true);
        } else {
            PlayList.loop("SND_FSLoop");
        }
    }

    spin(): void {
        StageController.baseStage = false;
        StageController.freespinStage = true;

        this._respin = false;

        if (this._fsMessageStart != null && !this._fsMessageStart.classList.contains(HtmlController.DisabledClass)) {
            this._fsMessageStart.classList.add(HtmlController.DisabledClass);
        }

        this._machineStopped = false;

        this._machine.spinStart(this._winAnimator.reset(false, true, false), false);
        this._winAnimator.removeAllCurrentSymbol();
        this._winAnimator.updateFreespinsCount(Settings.remainingFreespinsCount() - 1);
    }

    stop(): void {
        if (this._machineStopped) {
            if (Settings.isRespin()) {
                this._controller.winCalculationFinished(); // for show win
            } else {
                this._winAnimator.reset(true);
            }
        } else {
            this._machine.spinStop(null, true);
        }
    }

    startReceived(): void {
        if (this._fsMessageStart != null && !this._fsMessageStart.classList.contains(HtmlController.DisabledClass)) {
            this._fsMessageStart.classList.add(HtmlController.DisabledClass);
        }

        this._machine.setStoppedSymbolsArray(GameManager.freespinsResponse.screen.reels);

        this._winAnimator.removeAllCurrentSymbol(true);
        this._winAnimator.showSymbols(GameManager.freespinsResponse.screen.reels);
        this._winAnimator.reset(true);
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.freespinsResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    onReelStopped(): void {
        this._winAnimator.onReelStopped(GameManager.freespinsResponse.screen.reels, this._machine.actualReelStopped);
    }

    onRespinReelStopped(): void {
        this._winAnimator.onRespinReelStopped(
            GameManager.freespinsResponse.screen.reels,
            this._machine.actualReelStopped,
        );
    }

    onSpinReelRollFinished(reelIndex: number): void {
        this._winAnimator.onSpinReelRollFinished(GameManager.freespinsResponse.screen.reels, reelIndex);
    }

    public getRandom(min: number, max: number): number {
        return Math.random() * (max - min) + min;
    }

    machineStopped(respin: boolean = false): void {
        this._respin = respin;

        // show symbols after is respin completed whole, but normal spin symbols is showed in onReelStopped
        if (this._respin) {
            this._winAnimator.showSymbols(GameManager.freespinsResponse.screen.reels);
        }

        if (this._winAnimator.hasMystery()) {
            this._controller.beforeMachineStopped();
            this._mysteryDelayTimer.start(WinAnimatorExtended.MysterySymbolStartDelay);
            return;
        }

        this.machineStoppedDelayed();
    }

    private finishQuickWinCalculation(): void {
        this._controller.quickWinCalculationFinished();
    }

    mysterySymbol(): void {
        this._machineStoppedDelayTimer.stop();
        this._winAnimator.mysterySymbols();
        this._machineStoppedDelayTimer.start(WinAnimatorExtended.MysterySymbolAnimDelay);
    }

    machineStoppedDelayed(): void {
        this._machineStopped = true;
        this._controller.machineStopped(this._respin);
        this._winAnimator.machineStopped();

        if (
            this._winAnimator.showWins(
                GameManager.freespinsResponse.wins,
                GameManager.freespinsResponse.currentWin,
                GameManager.freespinsResponse.totalWin,
                GameManager.freespinsResponse.customData != null
                    ? GameManager.freespinsResponse.customData[Settings.CustomDataRoundTotalWin]
                    : 0,
                this._respin,
            )
        ) {
            // in HW
            PlayList.setVolume("SND_FSLoop", 0, 5);
            this._controller.highWins(); // for enable stop button
        }

        if (GameManager.freespinsResponse.currentWin === 0 && !this._respin) {
            if (this.isLastFreespin() && !Settings.isRespin() && !Settings.isNewCoins() && !this.isRetrigger()) {
                PlayList.stop("SND_FSLoop", 5);
                PlayList.loop("SND_FSWait", 5);
                PlayList.play("SND_FSEnd");

                this.fsMessageEnd(0);
            }
        }
    }

    limitWinCounter(isHighWin: boolean = false): void {
        if (isHighWin) {
            // in HW
            PlayList.setVolume("SND_FSLoop", 0, 5);
            this._controller.highWins(); // for enable stop button
        }
    }

    finishWinCalculation(): void {
        this._controller.winCalculationFinished();
    }

    finishWinAnimator(wasRetrigger: boolean = false): void {
        this._controller.allWinsFinished(wasRetrigger);

        if (this.isLastFreespin() && !Settings.isRespin() && !Settings.isNewCoins()) {
            PlayList.stop("SND_FSLoop", 5);
            PlayList.loop("SND_FSWait", 5);
            PlayList.play("SND_FSEnd");

            this.fsMessageEnd(0);
        }
    }

    intro(long: boolean = false, previousModule: string): void {
        StageController.baseStage = false;
        StageController.freespinStage = true;
        StageController.pickStage = false;

        this._winAnimator.updateCoinsCollector(true); // INIT coins collector
        this._winAnimator.updateFreespinsCount(Settings.remainingFreespinsCount());

        if (this._multiplierValueElement) {
            this._multiplierValueElement.setText("x" + GameManager.freespinsResponse.freeSpins.multiplier);
        }

        if (this._groupTweens) {
            this._groupTweens.goToCase(GroupTweens.CasePickToFreespinsIN);

            this._megaWaysSwitch.goToCase("INTRO_LEVEL_" + Settings.getCustomDataLevel());
        }

        this.visible = true;

        this._winAnimator.recountingWaysCounter(-1); // -1 get from reels

        this._introDelay.start(FreespinsView.INTRO_DELAY);
    }

    introFinished(): void {
        if (this._groupTweens) {
            this._groupTweens.goToCase(GroupTweens.CasePickToFreespinsOUT);
        }

        this._controller.introFinished();
    }

    outro(): void {
        StageController.freespinStage = true;

        this.visible = true;

        AmbientBackground.changeAmbientBackground("base", 90);

        PlayList.stop("SND_FSWait", 5);

        if (this._fsMessageEnd != null && !this._fsMessageEnd.classList.contains(HtmlController.DisabledClass)) {
            this._fsMessageEnd.classList.add(HtmlController.DisabledClass);
        }

        if (
            this._fsMessageEndNoWin != null &&
            !this._fsMessageEndNoWin.classList.contains(HtmlController.DisabledClass)
        ) {
            this._fsMessageEndNoWin.classList.add(HtmlController.DisabledClass);
        }

        if (this._groupTweens) {
            this._groupTweens.goToCase(GroupTweens.CaseFreespinsToBaseIN);
        }

        this._outroDelay.start(FreespinsView.OUTRO_DELAY);
    }

    outroFinished(): void {
        StageController.freespinStage = false;

        this.visible = false;

        this._winAnimator.reset(true);

        if (this._groupTweens) {
            this._groupTweens.goToCase(GroupTweens.CaseFreespinsToBaseOUT);
        }

        this._controller.outroFinished();
    }

    /* RESPIN */

    respinTeaseStart(): void {
        this._controller.respinTeaseStart();
    }

    respin(): void {
        this._machineStopped = false;

        this._machine.respinStart(
            this._winAnimator.reset(false, false, true),
            true,
            CustomUtils.getCurrentSymbolsCases(this._winAnimator.currentSymbols),
        );
        this._winAnimator.removeAllCurrentSymbol();
    }

    respinRollFinished(): void {
        this._controller.respinRollFinished();
    }

    enable(): void {
        // if (this._fsMessageStart != null && this._fsMessageStart.classList.contains(HtmlController.DisabledClass)) {
        //     this._fsMessageStart.classList.remove(HtmlController.DisabledClass);
        //     this._fsMessageStart.classList.remove("_noAnim");
        // }
    }

    private isRetrigger(): boolean {
        if (GameManager.freespinsResponse.wins != null && GameManager.freespinsResponse.wins.length > 0) {
            for (let i: number = 0; i < GameManager.freespinsResponse.wins.length; i++) {
                if (GameManager.freespinsResponse.wins[i].type === Communication.Game.WinType.freeSpins) {
                    return true;
                }
            }
        }

        return false;
    }

    public updateRemainingFreespinsCount(): void {
        this._controller.updateRemainingFreespinsCount();
    }

    private isLastFreespin(): boolean {
        if (DataManager.GameLimit) {
            return true;
        }

        return (
            GameManager.freespinsResponse.freeSpins != null &&
            GameManager.freespinsResponse.freeSpins.remainingCount != null &&
            GameManager.freespinsResponse.freeSpins.remainingCount === 0
        );
    }

    private setMode = function (mode: "landscape" | "portrait" | "extended") {
        switch (mode) {
            case "extended":
                if (this.modeSetting.Extended) {
                    this.updateByNode(this.modeSetting.Extended);
                    break;
                }
            case "portrait":
                if (this.modeSetting.Portrait) {
                    this.updateByNode(this.modeSetting.Portrait);
                    break;
                }
            case "landscape":
                if (this.modeSetting.Landscape) {
                    this.updateByNode(this.modeSetting.Landscape);
                }
                break;
        }
    };

    public fsMessageEnd(delay: number = 0, isInit: boolean = false): void {
        this._fsMessageEndDelayTimer.stop();

        if (delay != null) {
            if (delay == 0) {
                PlayList.play("SND_BaseMessage");
                if (GameManager.freespinsResponse.totalWin > 0) {
                    if (
                        this._fsMessageEnd != null &&
                        this._fsMessageEnd.classList.contains(HtmlController.DisabledClass)
                    ) {
                        this._fsMessageEnd.classList.remove(HtmlController.DisabledClass);
                        this._fsMessageEnd.classList.remove("_noAnim");
                    }
                } else {
                    if (
                        this._fsMessageEndNoWin != null &&
                        this._fsMessageEndNoWin.classList.contains(HtmlController.DisabledClass)
                    ) {
                        this._fsMessageEndNoWin.classList.remove(HtmlController.DisabledClass);
                        this._fsMessageEndNoWin.classList.remove("_noAnim");
                    }
                }

                if (this._groupTweens) {
                    let value: string = NumberUtils.valueToNumber(
                        GameManager.freespinsResponse.totalWin,
                        ImageTextCounterElement.Round ? 0 : AppContext.gameConfiguration.ShowDecimalPlaces,
                    );
                    this._fsMessageEndValueElement.setText(value); // UPDATE WIN VALUE

                    if (isInit) {
                        this._groupTweens.goToCase(GroupTweens.CaseFreespinsMessageEndInit);
                    } else {
                        this._groupTweens.goToCase(GroupTweens.CaseFreespinsMessageEnd);
                    }
                }
            } else if (delay > 0) {
                this._fsMessageEndDelayTimer.start(delay);
            }
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Freespins/FreespinsView.ts
 **/